import { SearchableSelect, SearchableSelectOption } from "@comps/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import Field, { FieldType, TYPE_ITEMS_RELATION_KEY } from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { Pivot, View } from "@dto/views";
import { BASE_URL } from "@utils/constants";
import { clearData } from "@utils/data";
import { getHiddenPathKeys, getPathKeys, getSrcFieldKey, hasOnlyOneValue } from "@utils/model";
import { toastError } from "@utils/toasts";
import { TW_FilterEditorPathSelect, TW_PivotEditor, TW_PivotEditorItem, TW_PivotEditorItemIcon, TW_PivotEditorItemIconDisabled, TW_PivotFieldFormLabel, TW_PivotItemValueEditor } from "@utils/twClasses";
import { put } from "@utils/utils";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";


interface PivotEditorProps {
    pivot: Pivot | null
    disabled: boolean
    onClose: () => void
}

function PivotEditor({ pivot, disabled, onClose }: PivotEditorProps) {

    const updateSelectedView = useModelStore((state) => state.updateSelectedView);
    const workspace = useModelStore((state) => state.selectedWorkspace);
    const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
    const sharedObjectInfos = useModelStore((state) => state.sharedObjectInfos);
    const selectedView = useModelStore((state) => state.selectedView);
    const [selectedPivotField, setSelectedPivotField] = useState<string | null>(null);
    const [selectedPivotValues, setSelectedPivotValues] = useState<string[] | null>(null);
    const relationByKeys = useModelStore((state) => state.relationByKeys);
    const fieldByKeys = useModelStore((state) => state.fieldByKeys);
    const { t } = useTranslation();

    useEffect(() => {

        const fields = selectedObjectInfos?.fields;
        if (!fields) {
            return;
        }
        const pathKeys = getPathKeys(selectedObjectInfos, fields, relationByKeys, fieldByKeys, true);
        const selectedPivotPathKeyIndex = pathKeys.findIndex(pathKey => pathKey === pivot?.pivotPathKey);
        if (selectedPivotPathKeyIndex > -1) {
            setSelectedPivotField(pivot?.pivotPathKey || null)
        } else {
            const pivotFieldKey = getSrcFieldKey(pivot?.pivotPathKey || "");
            setSelectedPivotField(pivotFieldKey?.fieldKey || null);
        }
        const selectedValuePathKeyIndex = pathKeys.findIndex(pathKey => pathKey === pivot?.valuePathKeys?.[0]);
        if (selectedValuePathKeyIndex > -1) {
            setSelectedPivotValues(pivot?.valuePathKeys || null);
        } else {
            const pivotValueKeys = pivot?.valuePathKeys?.map(valuePathKey => getSrcFieldKey(valuePathKey)?.fieldKey);
            setSelectedPivotValues(pivotValueKeys?.filter(valuePathKey => valuePathKey !== undefined) as string[] || null);
        }
    }, [pivot]);

    const fields = selectedObjectInfos?.fields;
    if (!fields || !selectedView || !workspace) {
        return null;
    }

    const pathKeys = getPathKeys(selectedObjectInfos, fields, relationByKeys, fieldByKeys, false);

    const onViewChange = (objectInfos: ObjectInfos, view: View) => {
        updateSelectedView(objectInfos, view, null, sharedObjectInfos);
        // No need to call fetchView as it is triggered by the view change
        // fetchView(view);
        // The lines on screen do not fit the new pivot: they are dropped before
        // being fetched again
        clearData();
    }

    const onPivotFieldChange = (pivotField: string | null) => {

        if (pivotField === selectedPivotField) {
            return;
        }

        let newPivot: Pivot | null = {
            pivotPathKey: pivotField,
            valuePathKeys: selectedPivotValues ? selectedPivotValues : null
        }

        if (pivotField == null) {
            newPivot = null;
        }

        setSelectedPivotField(pivotField);
        validatePivot(newPivot);
    }

    const onPivotValueChange = (pivotValue: string, index: number) => {
        if (!selectedPivotField || pivotValue === selectedPivotValues?.[index]) {
            return;
        }
        const newPivotValues: string[] = [...(selectedPivotValues || [])];
        if (newPivotValues.length <= index) {
            newPivotValues.push(pivotValue);
        } else {
            newPivotValues[index] = pivotValue;
        }
        setSelectedPivotValues(newPivotValues);
        validatePivot({
            pivotPathKey: selectedPivotField,
            valuePathKeys: newPivotValues
        });
    }

    const onPivotValueAdded = (value: string) => {
        let newPivotValues: string[] | null = [...(selectedPivotValues || [])];
        newPivotValues.push(value);
        setSelectedPivotValues(newPivotValues);
        validatePivot({
            pivotPathKey: selectedPivotField,
            valuePathKeys: newPivotValues
        });
    }

    const onPivotFieldDelete = () => {
        onPivotFieldChange(null);
    }

    const onPivotValueDelete = (index: number) => {
        let newPivotValues: string[] | null = [...(selectedPivotValues || [])];
        newPivotValues.splice(index, 1);
        if (newPivotValues.length === 0) {
            newPivotValues = null;
        }
        setSelectedPivotValues(newPivotValues);
        validatePivot({
            pivotPathKey: selectedPivotField,
            valuePathKeys: newPivotValues
        });
    }

    function validatePivot(newPivot: Pivot | null) {

        if (!workspace || !selectedObjectInfos || !selectedView) {
            return;
        }

        const newView: View = {
            ...selectedView,
            pivot: newPivot
        }
        put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${selectedObjectInfos?.object?._key}/views`, newView).then(response => {
            if (response) {
                if (isCompozzError(response)) {
                    const cError = response as CompozzError;
                    if (cError.i18n) {
                        toastError(translate(t, cError.i18n, cError.params));
                    } else {
                        toastError(cError.message);
                    }
                    onClose();
                }
            } else {
                onViewChange(selectedObjectInfos!, newView);
            }
        })
    }

    const trashFieldDisabled = disabled || !selectedPivotField;
    const selectValueDisabled = disabled || !selectedPivotField;

    // Ugly hack to force rerendering of the select when the pivot field or value is reset
    let selectFieldKey = selectedView?._key + "-pivot-field";
    if (!selectedPivotField) {
        selectFieldKey += "-null";
    }


    function isFieldPivotable(field: Field) {
        switch (field.type) {
            case FieldType.Choice:
            case FieldType.Link:
                return true;
            case FieldType.Date:
            case FieldType.Period:
                return true;
            case FieldType.Link:
                const relationKey = field.typeItems?.get(TYPE_ITEMS_RELATION_KEY);
                if (!relationKey) {
                    return false;
                }
                const relation = relationByKeys?.get(relationKey);
                return hasOnlyOneValue(relation, selectedObjectInfos?.object?._key);
            default:
                return false;
        }
    }

    const groupPathKeys = selectedView.groups?.map((group) => group.pathKey) ?? [];
    const hiddenPathKeys = getHiddenPathKeys(selectedView, pathKeys.filter(pathKey => pathKey !== null) as string[]);

    const newPivotValueKey = "new-pivot-value-" + selectedView?._key + "-" + (selectedPivotValues?.length ?? 0);
    return (
        <div className={TW_PivotEditor}>
            <div className={TW_PivotEditorItem}>
                <p className={TW_PivotFieldFormLabel}>{t("view.header.pivot.field.title")}</p>
                <div className={TW_PivotItemValueEditor}>
                    {disabled &&
                        <TooltipProvider>
                            <Tooltip delayDuration={100}>
                                <TooltipTrigger asChild>
                                    <Select key={selectFieldKey} onValueChange={onPivotFieldChange} value={selectedPivotField || undefined} disabled={disabled}>
                                        <SelectTrigger className={TW_FilterEditorPathSelect}>
                                            <SelectValue placeholder={t("view.header.pivot.field.placeholder")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {pathKeys.map((pathKey, index) => {
                                                const field = fields[index];
                                                if (!isFieldPivotable(field)) {
                                                    return null;
                                                }
                                                if (pathKey === null) {
                                                    return <SelectItem key={field._key} value={field._key} disabled={true}>{field.name} + " (" + t("group.field.invalid") + ")"</SelectItem>;
                                                }
                                                const disabled = false;
                                                return (
                                                    <SelectItem key={selectedView?._key + "-pivot-field-" + index} value={pathKey} disabled={disabled}>{field.name}</SelectItem>
                                                )
                                            })}
                                        </SelectContent>
                                    </Select>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {t("view.header.pivot.edition.forbidden.tooltip")}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    }
                    {!disabled &&
                        <SearchableSelect
                            key={selectFieldKey}
                            className={TW_FilterEditorPathSelect}
                            placeholder={t("view.header.pivot.field.placeholder")}
                            value={selectedPivotField}
                            onValueChange={onPivotFieldChange}
                            options={pathKeys.flatMap((pathKey, index): SearchableSelectOption[] => {
                                const field = fields[index];
                                if (!isFieldPivotable(field)) {
                                    return [];
                                }
                                if (pathKey === null) {
                                    return [{ value: field._key, label: field.name + " (" + t("group.field.invalid") + ")", disabled: true }];
                                }
                                const disabled = groupPathKeys.includes(pathKey) || hiddenPathKeys.includes(pathKey);
                                return [{ value: pathKey, label: field.name, disabled: disabled }];
                            })}
                        />
                    }
                    {!trashFieldDisabled && <Trash2 className={TW_PivotEditorItemIcon} onClick={onPivotFieldDelete} />}
                    {trashFieldDisabled && <Trash2 className={TW_PivotEditorItemIconDisabled} />}
                </div>
            </div>
            <div className={TW_PivotEditorItem}>
                <p className={TW_PivotFieldFormLabel}>{t("view.header.pivot.value.title")}</p>
                {selectedPivotValues?.map((pivotValue, index) => {
                    const onValueChange = (value: string) => {
                        onPivotValueChange(value, index);
                    }
                    const onValueDelete = () => {
                        onPivotValueDelete(index);
                    }

                    const trashValueDisabled = disabled || !selectedPivotField || !pivotValue;
                    let selectValueKey = selectedView?._key + "-pivot-value";
                    if (!pivotValue) {
                        selectValueKey += "-null";
                    }
                    return (
                        <div className={TW_PivotItemValueEditor}>
                            {disabled &&
                                <TooltipProvider>
                                    <Tooltip delayDuration={100}>
                                        <TooltipTrigger asChild>
                                            <Select key={selectValueKey} onValueChange={onValueChange} value={pivotValue || undefined} disabled={selectValueDisabled}>
                                                <SelectTrigger className={TW_FilterEditorPathSelect}>
                                                    <SelectValue placeholder={t("view.header.pivot.value.placeholder")} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {pathKeys.map((pathKey, index) => {
                                                        const field = fields[index];
                                                        if (pathKey === null) {
                                                            return <SelectItem key={field._key} value={field._key} disabled={true}>{field.name} + " (" + t("group.field.invalid") + ")"</SelectItem>;

                                                        }
                                                        const disabled = pathKey === selectedPivotField || selectedPivotValues?.includes(pathKey);
                                                        return (
                                                            <SelectItem key={selectedView?._key + "-pivot-value-" + index} value={pathKey} disabled={disabled}>{field.name}</SelectItem>
                                                        )
                                                    })}
                                                </SelectContent>
                                            </Select>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {t("view.header.pivot.edition.forbidden.tooltip")}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            }
                            {!disabled &&
                                <SearchableSelect
                                    key={selectValueKey}
                                    className={TW_FilterEditorPathSelect}
                                    placeholder={t("view.header.pivot.value.placeholder")}
                                    value={pivotValue}
                                    onValueChange={onValueChange}
                                    disabled={selectValueDisabled}
                                    options={pathKeys.flatMap((pathKey, index): SearchableSelectOption[] => {
                                        const field = fields[index];
                                        if (pathKey === null) {
                                            return [{ value: field._key, label: field.name + " (" + t("group.field.invalid") + ")", disabled: true }];
                                        }
                                        const disabled = pathKey === selectedPivotField || hiddenPathKeys.includes(pathKey) || groupPathKeys.includes(pathKey) || selectedPivotValues?.includes(pathKey);
                                        return [{ value: pathKey, label: field.name, disabled: disabled }];
                                    })}
                                />
                            }
                            {!trashValueDisabled && <Trash2 className={TW_PivotEditorItemIcon} onClick={onValueDelete} />}
                            {trashValueDisabled && <Trash2 className={TW_PivotEditorItemIconDisabled} />}
                        </div>
                    )
                })}
                <div className={TW_PivotItemValueEditor}>
                    {disabled &&
                        <TooltipProvider>
                            <Tooltip delayDuration={100}>
                                <TooltipTrigger asChild>
                                    <Select key={newPivotValueKey} onValueChange={onPivotValueAdded} value={undefined} disabled={selectValueDisabled}>
                                        <SelectTrigger className={TW_FilterEditorPathSelect}>
                                            <SelectValue placeholder={t("view.header.pivot.value.placeholder")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {pathKeys.map((pathKey, index) => {
                                                const field = fields[index];
                                                if (pathKey === null) {
                                                    return <SelectItem key={field._key} value={field._key} disabled={true}>{field.name + " (" + t("group.field.invalid") + ")"}</SelectItem>;

                                                }
                                                const disabled = pathKey === selectedPivotField || groupPathKeys.includes(pathKey) || selectedPivotValues?.includes(pathKey);
                                                return (
                                                    <SelectItem key={selectedView?._key + "-pivot-value-" + index} value={pathKey} disabled={disabled}>{field.name}</SelectItem>
                                                )
                                            })}
                                        </SelectContent>
                                    </Select>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {t("view.header.pivot.edition.forbidden.tooltip")}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    }
                    {!disabled &&
                        <SearchableSelect
                            key={newPivotValueKey}
                            className={TW_FilterEditorPathSelect}
                            placeholder={t("view.header.pivot.value.placeholder")}
                            onValueChange={onPivotValueAdded}
                            options={pathKeys.flatMap((pathKey, index): SearchableSelectOption[] => {
                                const field = fields[index];
                                if (pathKey === null) {
                                    return [{ value: field._key, label: field.name + " (" + t("group.field.invalid") + ")", disabled: true }];
                                }
                                const disabled = pathKey === selectedPivotField || groupPathKeys.includes(pathKey) || selectedPivotValues?.includes(pathKey);
                                return [{ value: pathKey, label: field.name, disabled: disabled }];
                            })}
                        />
                    }
                </div>
            </div>
        </div>
    )
}

export default PivotEditor;
